import { useMemo, useState } from 'react';
import {
  useClientChurn,
  useClients,
  useClientStats,
  useClientTiers,
  useLatestClientPhotoUrls,
  useRegularConversion,
  useTopClients,
  type ClientRow,
  type Tier,
} from '@busy/api';
import { Avatar, TierBadge } from '@busy/ui';
import { useAcquisitionChannels } from '../../lib/useAcquisitionChannels';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import {
  daysUntilBirthday,
  formatBirthdayShort,
} from '../../lib/birthday';
import { HelpHint } from '../HelpHint';
import { BigTipperBadge } from './BigTipperBadge';
import { RevenueTierBadge } from './RevenueTierBadge';

// Your Book — the consolidated client-lifecycle tile on the Clients
// tab. Replaces four standalone tiles (Retention, Acquisition,
// Birthdays, Top Clients) with a single surface organized around
// two questions:
//
//   1. THE OVERVIEW (top half, two columns):
//      Left  — "Where they came from" (acquisition channels)
//      Right — "How they move through" (convert % + lost %)
//
//   2. THE ACTION LISTS (bottom half, three collapsibles):
//      Drifting clients — at-risk regulars who can still be saved
//      Upcoming birthdays — outreach opportunities
//      Top clients — your VIPs by revenue
//
// Why one tile: the original layout had FOUR separate cards stacked
// vertically, each with its own border and header. Combining them
// here gives a cleaner narrative ("here's your book") and lets the
// user scan the macro story (top) and the action items (bottom) in
// one place.
//
// All hooks are existing — no new data fetching. Old standalone tiles
// got deleted as part of this consolidation.

export function ClientPipelineTile({
  onOpenClient,
}: {
  onOpenClient: (clientId: string) => void;
}) {
  const t = useT();
  return (
    <div className="rounded-2xl border border-line bg-surface p-4">
      {/* Header */}
      <div className="mb-4">
        <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500 inline-flex items-center gap-1.5">
          <span>{t('clients.book.eyebrow')}</span>
          <HelpHint
            titleKey="clients.book.help.title"
            bodyKey="clients.book.help.body"
            ariaKey="clients.book.help.aria"
          />
        </div>
        <h2 className="font-display text-lg leading-tight mt-0.5">
          {t('clients.book.title')}
        </h2>
      </div>

      {/* ────────── OVERVIEW (2-col grid, stacks on mobile) ────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <AcquisitionColumn />
        <RetentionColumn />
      </div>

      {/* ────────── ACTION LISTS ────────── */}
      <div className="mt-5 space-y-3 border-t border-line pt-4">
        <DriftingSection onOpenClient={onOpenClient} />
        <BirthdaysSection onOpenClient={onOpenClient} />
        <TopClientsSection onOpenClient={onOpenClient} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// OVERVIEW — left column: acquisition channels
// ════════════════════════════════════════════════════════════

function AcquisitionColumn() {
  const t = useT();
  const { data, loading } = useAcquisitionChannels();

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-ink-500 mb-2">
        {t('clients.book.acquisition.label')}
      </div>
      {loading ? (
        <div className="h-32 rounded-lg bg-bg-alt animate-pulse" />
      ) : !data || data.insufficient ? (
        <p className="text-[12px] text-ink-500 leading-snug">
          {t('clients.book.acquisition.empty')}
        </p>
      ) : (
        // Show top 4 channels (excluding untagged) with mini bars.
        // The full 8-row breakdown lived on the old AcquisitionChannelTile;
        // here we condense to the leading channels since this is the
        // overview, not the deep-dive.
        <ul className="space-y-1.5">
          {data.rows
            .filter((r) => r.source !== 'untagged' && r.clientCount > 0)
            .slice(0, 4)
            .map((row) => {
              const barPct = Math.min(100, Math.round(row.sharePct));
              return (
                <li key={row.source} className="space-y-0.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[12px] font-bold text-ink-900 dark:text-ink-100 truncate">
                      {t(`acquisitionSource.${row.source}`)}
                    </span>
                    <span className="text-[10px] text-ink-500 tabular-nums shrink-0">
                      {row.clientCount} · {Math.round(row.sharePct)}%
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-bg-alt overflow-hidden">
                    <div
                      className="h-full bg-navy-900 dark:bg-gold-500 transition-all"
                      style={{ width: `${barPct}%` }}
                    />
                  </div>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// OVERVIEW — right column: retention stats
// ════════════════════════════════════════════════════════════

function RetentionColumn() {
  const t = useT();
  const conversion = useRegularConversion();
  const churn = useClientChurn();

  if (conversion.loading || churn.loading) {
    return <div className="h-32 rounded-lg bg-bg-alt animate-pulse" />;
  }

  const hasConversion =
    conversion.data &&
    conversion.data.cohort_size > 0 &&
    conversion.data.conversion_pct != null;
  const hasChurn =
    churn.data &&
    churn.data.total_regulars > 0 &&
    churn.data.churn_pct != null;

  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-ink-500 mb-2">
        {t('clients.book.retention.label')}
      </div>
      {!hasConversion && !hasChurn ? (
        <p className="text-[12px] text-ink-500 leading-snug">
          {t('clients.book.retention.empty')}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {hasConversion && (
            <ConversionStat
              pct={conversion.data!.conversion_pct!}
              converted={conversion.data!.converted_count}
              cohort={conversion.data!.cohort_size}
              recentPct={conversion.data!.recent_conversion_pct}
              recentCohort={conversion.data!.recent_cohort_size}
            />
          )}
          {hasChurn && (
            <ChurnStat
              pct={churn.data!.churn_pct!}
              lapsed={churn.data!.lapsed_regulars}
              total={churn.data!.total_regulars}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ConversionStat({
  pct,
  converted,
  cohort,
  recentPct,
  recentCohort,
}: {
  pct: number;
  converted: number;
  cohort: number;
  recentPct: number | null;
  recentCohort: number;
}) {
  const t = useT();
  const hasTrend = recentPct != null && recentCohort >= 3;
  const trend: 'up' | 'down' | 'flat' | null = hasTrend
    ? recentPct! > pct + 5
      ? 'up'
      : recentPct! < pct - 5
        ? 'down'
        : 'flat'
    : null;
  return (
    <div className="rounded-lg border border-line bg-bg-alt p-2.5">
      <div className="font-display text-2xl tabular-nums leading-none text-navy-900 dark:text-gold-500">
        {Math.round(pct)}%
      </div>
      <div className="text-[9px] uppercase tracking-[0.08em] text-ink-500 font-bold mt-1 leading-tight">
        {t('clients.retention.convert.label')}
      </div>
      <p className="text-[10px] text-ink-700 dark:text-ink-300 mt-1 leading-snug tabular-nums">
        {converted}/{cohort}
      </p>
      {trend && (
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] uppercase tracking-[0.06em] font-extrabold mt-1.5 ${
            trend === 'up'
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
              : trend === 'down'
                ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-bg-alt text-ink-700'
          }`}
        >
          {t(`clients.retention.trend.${trend}`)}
        </span>
      )}
    </div>
  );
}

function ChurnStat({
  pct,
  lapsed,
  total,
}: {
  pct: number;
  lapsed: number;
  total: number;
}) {
  const t = useT();
  const tone: 'good' | 'mid' | 'bad' =
    pct < 20 ? 'good' : pct < 35 ? 'mid' : 'bad';
  const numberCls =
    tone === 'good'
      ? 'text-emerald-700 dark:text-emerald-400'
      : tone === 'mid'
        ? 'text-amber-700 dark:text-amber-400'
        : 'text-red-700 dark:text-red-400';
  const pillCls =
    tone === 'good'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
      : tone === 'mid'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  return (
    <div className="rounded-lg border border-line bg-bg-alt p-2.5">
      <div className={`font-display text-2xl tabular-nums leading-none ${numberCls}`}>
        {Math.round(pct)}%
      </div>
      <div className="text-[9px] uppercase tracking-[0.08em] text-ink-500 font-bold mt-1 leading-tight">
        {t('clients.retention.churn.label')}
      </div>
      <p className="text-[10px] text-ink-700 dark:text-ink-300 mt-1 leading-snug tabular-nums">
        {lapsed}/{total}
      </p>
      <span
        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] uppercase tracking-[0.06em] font-extrabold mt-1.5 ${pillCls}`}
      >
        {t(`clients.retention.health.${tone}`)}
      </span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// ACTION LIST — Drifting clients (formerly the at-risk tile)
// ════════════════════════════════════════════════════════════

type DriftingRow = {
  client: ClientRow;
  daysSince: number | null;
  totalRevenueCents: number;
  isBigTipper: boolean;
  totalTipsCents: number;
};

function DriftingSection({
  onOpenClient,
}: {
  onOpenClient: (clientId: string) => void;
}) {
  const t = useT();
  const tiers = useClientTiers();
  const stats = useClientStats();
  const clients = useClients();
  const photoUrls = useLatestClientPhotoUrls();

  const rows = useMemo<DriftingRow[]>(() => {
    const tierMap = new Map<string, string>(
      (tiers.data ?? []).map((r) => [r.client_id, r.tier]),
    );
    const statsMap = new Map((stats.data ?? []).map((s) => [s.client_id, s]));
    const out: DriftingRow[] = [];
    for (const c of clients.data ?? []) {
      if (tierMap.get(c.id) !== 'at-risk') continue;
      const s = statsMap.get(c.id);
      out.push({
        client: c,
        daysSince: s?.days_since_last_visit ?? null,
        totalRevenueCents: s?.total_revenue_cents ?? 0,
        isBigTipper: !!s?.is_big_tipper,
        totalTipsCents: s?.total_tips_cents ?? 0,
      });
    }
    out.sort((a, b) => {
      if (a.isBigTipper !== b.isBigTipper) return a.isBigTipper ? -1 : 1;
      return (b.daysSince ?? 0) - (a.daysSince ?? 0);
    });
    return out;
  }, [tiers.data, stats.data, clients.data]);

  const count = rows.length;
  const loading = tiers.loading || stats.loading || clients.loading;
  // Default-open when there's something to act on.
  const [open, setOpen] = useState(count > 0);

  return (
    <CollapsibleSection
      icon={<AlertIcon className={`w-4 h-4 shrink-0 ${count > 0 ? 'text-red-600 dark:text-red-400' : 'text-ink-500'}`} />}
      title={t('clients.retention.drifting.title')}
      sub={t('clients.retention.drifting.sub')}
      count={count}
      countTone={count > 0 ? 'red' : 'neutral'}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      {loading ? (
        <SkeletonList rows={2} />
      ) : count === 0 ? (
        <EmptyState text={t('clients.retention.drifting.allClear')} />
      ) : (
        <ul className="space-y-2">
          {rows.map((row) => {
            const name =
              `${row.client.first_name} ${row.client.last_name}`.trim() ||
              t('today.unnamed');
            return (
              <li key={row.client.id}>
                <button
                  type="button"
                  onClick={() => onOpenClient(row.client.id)}
                  className="w-full text-left rounded-xl border border-red-200 dark:border-red-600/40 bg-red-50/40 dark:bg-red-600/10 p-3 flex items-center gap-3 hover:border-red-400 dark:hover:border-red-500/60 transition-colors"
                >
                  <Avatar
                    name={name}
                    size="md"
                    tier="at-risk"
                    photoUrl={photoUrls.data?.[row.client.id]}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong className="truncate">{name}</strong>
                      <TierBadge tier="at-risk" size="sm" label={t('tier.at-risk')} />
                      {row.isBigTipper && <BigTipperBadge size="sm" />}
                    </div>
                    <span className="text-xs text-ink-500">
                      {row.daysSince != null
                        ? t('clients.atRisk.lastSeen', { days: Math.round(row.daysSince) })
                        : t('clients.atRisk.lastSeenUnknown')}
                      {row.isBigTipper && row.totalTipsCents > 0 && (
                        <span className="ml-1.5 text-green-600 dark:text-green-400 font-bold">
                          {t('clients.atRisk.tipLifetime', {
                            amount: `$${(row.totalTipsCents / 100).toFixed(0)}`,
                          })}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-base text-navy-900 dark:text-gold-500 tabular-nums">
                      ${(row.totalRevenueCents / 100).toFixed(0)}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.08em] text-ink-500 font-bold">
                      {t('clients.atRisk.lifetime')}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </CollapsibleSection>
  );
}

// ════════════════════════════════════════════════════════════
// ACTION LIST — Upcoming birthdays
// ════════════════════════════════════════════════════════════

type BirthdayWindow = 'today' | '7d' | '14d' | '30d';
const BIRTHDAY_WINDOWS: BirthdayWindow[] = ['today', '7d', '14d', '30d'];
const BIRTHDAY_WINDOW_DAYS: Record<BirthdayWindow, number> = {
  today: 0,
  '7d': 7,
  '14d': 14,
  '30d': 30,
};

type BirthdayRow = {
  client: ClientRow;
  daysAway: number;
  displayDate: string;
};

function BirthdaysSection({
  onOpenClient,
}: {
  onOpenClient: (clientId: string) => void;
}) {
  const t = useT();
  const clients = useClients();
  const photoUrls = useLatestClientPhotoUrls();
  const language = useUI((s) => s.language);
  const [window, setWindow] = useState<BirthdayWindow>('30d');

  const allWithBirthday = useMemo<BirthdayRow[]>(() => {
    const out: BirthdayRow[] = [];
    for (const c of clients.data ?? []) {
      const n = daysUntilBirthday(c.birthday_month, c.birthday_day);
      if (n == null) continue;
      const display = formatBirthdayShort(c.birthday_month, c.birthday_day, language);
      if (display == null) continue;
      out.push({ client: c, daysAway: n, displayDate: display });
    }
    out.sort((a, b) => a.daysAway - b.daysAway);
    return out;
  }, [clients.data, language]);

  const rowsInWindow = useMemo(
    () => allWithBirthday.filter((r) => r.daysAway <= BIRTHDAY_WINDOW_DAYS[window]),
    [allWithBirthday, window],
  );

  const count = rowsInWindow.length;
  const totalWithBirthday = allWithBirthday.length;
  const loading = clients.loading;
  const [open, setOpen] = useState(allWithBirthday.some((r) => r.daysAway <= 30));

  return (
    <CollapsibleSection
      icon={<CakeIcon className="w-4 h-4 shrink-0 text-navy-900 dark:text-gold-500" />}
      title={t('clients.birthdays.title')}
      sub={t('clients.birthdays.sub')}
      count={totalWithBirthday}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      <div className="space-y-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {BIRTHDAY_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWindow(w)}
              className={`h-8 px-3 rounded-full text-[12px] font-bold transition-colors ${
                window === w
                  ? 'bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900'
                  : 'bg-bg-alt text-ink-700 hover:bg-bg-alt/70'
              }`}
            >
              {t(`clients.birthdays.window.${w}`)}
            </button>
          ))}
        </div>
        {loading ? (
          <SkeletonList rows={2} />
        ) : totalWithBirthday === 0 ? (
          <div className="rounded-xl border border-dashed border-line p-5 text-center">
            <p className="text-sm text-ink-700 mb-1">
              {t('clients.birthdays.emptyAllTitle')}
            </p>
            <p className="text-xs text-ink-500 leading-snug">
              {t('clients.birthdays.emptyAllSub')}
            </p>
          </div>
        ) : count === 0 ? (
          <EmptyState text={t('clients.birthdays.emptyWindow')} />
        ) : (
          <ul className="space-y-2">
            {rowsInWindow.map((row) => {
              const name =
                `${row.client.first_name} ${row.client.last_name}`.trim() ||
                t('today.unnamed');
              const whenLabel =
                row.daysAway === 0
                  ? t('clients.birthdays.when.today')
                  : row.daysAway === 1
                    ? t('clients.birthdays.when.tomorrow')
                    : t('clients.birthdays.when.inDays', { n: row.daysAway });
              const isToday = row.daysAway === 0;
              return (
                <li key={row.client.id}>
                  <button
                    type="button"
                    onClick={() => onOpenClient(row.client.id)}
                    className={`w-full text-left rounded-xl border bg-surface p-3 flex items-center gap-3 transition-colors ${
                      isToday
                        ? 'border-gold-500/70 hover:border-gold-500'
                        : 'border-line hover:border-ink-500/40'
                    }`}
                  >
                    <Avatar
                      name={name}
                      size="md"
                      photoUrl={photoUrls.data?.[row.client.id]}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <strong className="truncate">{name}</strong>
                        {isToday && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-gold-500 text-navy-900 text-[10px] font-extrabold uppercase tracking-wide">
                            <CakeIcon className="w-2.5 h-2.5" />
                            <span>{t('clients.birthdays.todayBadge')}</span>
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-ink-500">
                        {row.displayDate} · {whenLabel}
                      </span>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 text-ink-500 shrink-0" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </CollapsibleSection>
  );
}

// ════════════════════════════════════════════════════════════
// ACTION LIST — Top clients
// ════════════════════════════════════════════════════════════

function TopClientsSection({
  onOpenClient,
}: {
  onOpenClient: (clientId: string) => void;
}) {
  const t = useT();
  const topClients = useTopClients(5);
  const tiers = useClientTiers();
  const photoUrls = useLatestClientPhotoUrls();
  const [open, setOpen] = useState(false);

  const tierByClient = useMemo(() => {
    const m = new Map<string, Tier>();
    (tiers.data ?? []).forEach((tt) => m.set(tt.client_id, tt.tier));
    return m;
  }, [tiers.data]);

  const list = topClients.data ?? [];
  const count = list.length;

  return (
    <CollapsibleSection
      icon={
        <CrownIcon
          className={`w-4 h-4 shrink-0 ${
            count > 0 ? 'text-gold-600 dark:text-gold-400' : 'text-ink-500'
          }`}
        />
      }
      title={t('dash.topClients.title')}
      sub={t('dash.topClients.sub')}
      count={count}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      {topClients.loading ? (
        <SkeletonList rows={3} />
      ) : topClients.error ? (
        <div className="rounded-lg border border-red-100 dark:border-red-600/40 bg-red-50 dark:bg-red-600/15 text-red-700 dark:text-red-300 p-3 text-sm">
          {topClients.error}
        </div>
      ) : count === 0 ? (
        <EmptyState text={t('dash.topClients.empty')} />
      ) : (
        <ul className="space-y-2">
          {list.map((item, idx) => {
            const name =
              `${item.client.first_name} ${item.client.last_name}`.trim() ||
              t('today.unnamed');
            const tier = tierByClient.get(item.client_id);
            const visitText =
              item.visit_count === 1
                ? t('dash.topClients.visit_one')
                : t('dash.topClients.visit_other', { count: item.visit_count });
            return (
              <li key={item.client_id}>
                <button
                  type="button"
                  onClick={() => onOpenClient(item.client_id)}
                  className="w-full text-left rounded-xl border border-line bg-surface p-3 flex items-center gap-3 hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 grid place-items-center font-display text-sm shrink-0">
                    {idx + 1}
                  </div>
                  <Avatar
                    name={name}
                    size="md"
                    tier={tier}
                    photoUrl={photoUrls.data?.[item.client_id]}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong className="truncate">{name}</strong>
                      {tier && <TierBadge tier={tier} size="sm" label={t(`tier.${tier}`)} />}
                      {item.revenue_tier && <RevenueTierBadge tier={item.revenue_tier} />}
                    </div>
                    <span className="text-xs text-ink-500">{visitText}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-lg text-navy-900 dark:text-gold-500 tabular-nums">
                      ${(item.total_revenue_cents / 100).toFixed(0)}
                    </div>
                    <div className="text-[11px] text-ink-500 tabular-nums">
                      ${((item.avg_ticket_cents ?? 0) / 100).toFixed(0)} {t('dash.topClients.avgSuffix')}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </CollapsibleSection>
  );
}

// ════════════════════════════════════════════════════════════
// Shared sub-section chrome — collapsible row that the three
// action lists above share. Keeps the visual rhythm consistent
// across sections without duplicating the header markup.
// ════════════════════════════════════════════════════════════

function CollapsibleSection({
  icon,
  title,
  sub,
  count,
  countTone = 'neutral',
  open,
  onToggle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  sub: string;
  count: number;
  countTone?: 'neutral' | 'red';
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const countCls =
    countTone === 'red'
      ? count > 0
        ? 'text-red-600 dark:text-red-400'
        : 'text-ink-500'
      : 'text-ink-500';
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 text-left rounded-md hover:bg-bg-alt/40 -m-1 p-1 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {icon}
            <strong className="font-display text-base leading-tight">{title}</strong>
          </div>
          <p className="text-[12px] text-ink-500 mt-0.5 leading-snug">{sub}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`text-[11px] font-semibold tabular-nums ${countCls}`}>{count}</span>
          <ChevronDownIcon
            className={`w-4 h-4 text-ink-500 transition-transform ${open ? '' : '-rotate-90'}`}
          />
        </div>
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function SkeletonList({ rows }: { rows: number }) {
  return (
    <ul className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <li key={i} className="h-16 rounded-xl bg-bg-alt animate-pulse" />
      ))}
    </ul>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-dashed border-line p-5 text-center text-sm text-ink-500">
      {text}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// Icons
// ════════════════════════════════════════════════════════════

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function CakeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
      <path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1" />
      <path d="M2 21h20" />
      <path d="M7 8v3M12 8v3M17 8v3M7 4v1M12 3v2M17 4v1" />
    </svg>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 18l2-9 5 4 3-7 3 7 5-4 2 9H2z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
